import { useState } from "react";
import { getAuditBase } from "../audit/AuditApp";
import { STATUSES, adminFetch, downloadBlob, fmtDateTime, lvl } from "./adminApi";

export function AuditRow({ audit, adminKey, emailEnabled, onUpdated, onDeleted, onError, onNotice }) {
  const [busy, setBusy] = useState("");
  const c = audit.contact || {};
  const date = new Date(audit.created_at);
  const reportPath = `${getAuditBase()}/rapport/${audit.id}`;
  const status = audit.status || "nouveau";

  async function run(kind, fn) {
    setBusy(kind);
    try { await fn(); } catch (e) { onError(e.message || "Une erreur est survenue."); } finally { setBusy(""); }
  }

  function pdf() {
    run("pdf", () => downloadBlob(`/audits/${audit.id}/pdf`, adminKey, `synthese-${audit.id.slice(0, 8)}.pdf`));
  }

  function changeStatus(value) {
    run("status", async () => {
      const res = await adminFetch(`/audits/${audit.id}/status`, adminKey, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status: value }),
      });
      onUpdated(await res.json());
    });
  }

  function sendReport() {
    const already = audit.report_sent_at ? `\n(déjà envoyé le ${fmtDateTime(audit.report_sent_at)})` : "";
    if (!window.confirm(`Envoyer la synthèse PDF à ${c.email} ?${already}`)) return;
    run("send", async () => {
      const res = await adminFetch(`/audits/${audit.id}/send-report`, adminKey, { method: "POST" });
      onUpdated(await res.json());
      onNotice(`Synthèse envoyée à ${c.email}.`);
    });
  }

  function remove() {
    if (!window.confirm(`Supprimer définitivement l'audit de ${c.company || "ce prospect"} ?\nCette action est irréversible (RGPD).`)) return;
    run("del", async () => {
      await adminFetch(`/audits/${audit.id}`, adminKey, { method: "DELETE" });
      onDeleted(audit.id);
    });
  }

  return (
    <tr data-testid={`admin-row-${audit.id}`} className={busy ? "is-busy" : ""}>
      <td className="admin-td-date">
        {date.toLocaleDateString("fr-FR")}<br />
        <span className="admin-muted">{date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
      </td>
      <td>
        <strong>{c.company}</strong><br />
        <span className="admin-muted">{c.full_name}{c.role ? ` · ${c.role}` : ""}{c.headcount ? ` · ${c.headcount} sal.` : ""}</span>
      </td>
      <td>
        <a href={`mailto:${c.email}`} className="admin-link">{c.email}</a><br />
        <span className="admin-muted">{c.phone || "—"}</span>
      </td>
      <td className="admin-td-score">
        <span className={`admin-score lvl-${lvl(audit.overall_score)}`} data-testid="admin-score">{audit.overall_score}</span>
      </td>
      <td className="admin-td-status">
        <select className={`admin-status admin-status-${status}`} value={status} disabled={busy === "status"}
          onChange={(e) => changeStatus(e.target.value)} data-testid={`admin-status-${audit.id}`}>
          {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        {audit.report_sent_at && (
          <div className="admin-muted admin-sent-at" data-testid={`admin-sent-at-${audit.id}`}>
            Envoyé le {fmtDateTime(audit.report_sent_at)}{audit.report_sent_count > 1 ? ` (×${audit.report_sent_count})` : ""}
          </div>
        )}
      </td>
      <td className="admin-td-actions">
        <div className="admin-actions">
          <button className="audit-btn audit-btn-primary admin-btn-sm" onClick={pdf} disabled={!!busy}
            data-testid={`admin-pdf-${audit.id}`}>{busy === "pdf" ? "…" : "PDF"}</button>
          <button className="audit-btn audit-btn-secondary admin-btn-sm" onClick={sendReport}
            disabled={!!busy || !emailEnabled}
            title={emailEnabled ? "Envoyer la synthèse PDF au prospect par email" : "SMTP non configuré côté serveur"}
            data-testid={`admin-send-${audit.id}`}>{busy === "send" ? "Envoi…" : "Envoyer au prospect"}</button>
          <a href={reportPath} target="_blank" rel="noopener noreferrer" className="audit-btn audit-btn-ghost admin-btn-sm"
            data-testid={`admin-view-${audit.id}`}>Rapport</a>
          <button className="audit-btn audit-btn-ghost admin-btn-sm admin-btn-danger" onClick={remove} disabled={!!busy}
            data-testid={`admin-delete-${audit.id}`}>{busy === "del" ? "…" : "Supprimer"}</button>
        </div>
      </td>
    </tr>
  );
}
